import { footer } from "../chrome";
import {
  aboutBlurb,
  aboutCards,
  aboutHead,
  aboutLead,
  faqCard,
} from "../content/render";
import { ABOUT_PAGES, aboutHref, type AboutPageId } from "../destinations";
import { el } from "../dom";

/**
 * What this is, honestly, including the parts that do not exist yet.
 *
 * A hub rather than one long page: the short description, and a card into each
 * of the pages under it. Each of those is an address of its own — `#about/faq`
 * — so a question can be linked to, and every one of them carries the same row
 * of tabs, which is what makes the set navigable once you are inside it.
 *
 * The copy is in content/about.json, except the FAQ's, which is faq.json.
 */

/** The tabs, on the hub and on every page under it. */
function subnav(page: AboutPageId | null): HTMLElement {
  const tab = (href: string, label: string, current: boolean) => {
    const link = el("a", { href, textContent: label });
    // The same marker the masthead uses. About stays marked up there while a
    // page under it is marked down here: one says which destination, the other
    // says where in it.
    if (current) link.setAttribute("aria-current", "page");
    return link;
  };

  return el("nav", { className: "subnav", "aria-label": aboutHead.title }, [
    tab("/#about", "Overview", page === null),
    ...ABOUT_PAGES.map((entry) =>
      tab(aboutHref(entry.id), entry.label, entry.id === page),
    ),
  ]);
}

/** The hub: what this is, then the way into each page. */
function hub(): Node[] {
  return [
    aboutLead(),
    el(
      "div",
      { className: "about-pages" },
      ABOUT_PAGES.map((entry) =>
        el("a", { className: "page-card", href: aboutHref(entry.id) }, [
          el("h2", { textContent: entry.label }),
          aboutBlurb(entry.id),
        ]),
      ),
    ),
  ];
}

/**
 * One page under the hub. The FAQ is the questions; the rest are their cards.
 */
function pageBody(page: AboutPageId): Node[] {
  return page === "faq" ? [faqCard()] : aboutCards(page);
}

export function aboutScreen(page: AboutPageId | null): Node[] {
  // No page head: "About lance.blue" repeated the nav item that got the player
  // here, and the tabs below say which of these pages they are on. The h1 the
  // route still owes a screen reader is hidden, and names the page within
  // About rather than only About, so the tab and the heading agree.
  const label = ABOUT_PAGES.find((entry) => entry.id === page)?.label;
  return [
    el("h1", {
      className: "visually-hidden",
      textContent: label ? `${aboutHead.title}: ${label}` : aboutHead.title,
    }),
    subnav(page),
    ...(page === null ? hub() : pageBody(page)),
    footer(),
  ];
}
